import {
  ApiResponse,
  ClinicGroup,
  PaginationQuery,
  PaginationResponse,
} from '@magnetar/admin-api-interfaces'
import { useAuth } from './useAuth'
import { useGet } from './useFetch'

interface UseClinicGroupsParams extends PaginationQuery {
  is_active?: boolean
  provider_id?: number
}

export function useClinicGroups({
  page = 1,
  pageSize = 10,
  is_active,
  provider_id,
}: UseClinicGroupsParams) {
  const { token } = useAuth()

  const queryParams = new URLSearchParams()
  queryParams.append('page', String(page))
  queryParams.append('pageSize', String(pageSize))
  if (is_active !== undefined) queryParams.append('is_active', String(is_active))
  if (provider_id !== undefined) queryParams.append('provider_id', String(provider_id))

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<ClinicGroup>>>(
    token ? `/api/clinic-groups?${queryParams.toString()}` : null,
    undefined,
    token ?? '',
  )

  return {
    clinicGroups: data?.data?.items,
    pagination: data?.data
      ? {
          page: data.data.page,
          pageSize: data.data.pageSize,
          total: data.data.total,
          totalPages: data.data.totalPages,
        }
      : undefined,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

export function useClinicGroup(id: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<ClinicGroup>>(
    token && id ? `/api/clinic-groups/${id}` : null,
    undefined,
    token ?? '',
  )

  return {
    clinicGroup: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get clinics by clinic group
export function useClinicGroupClinics(groupId: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<
    ApiResponse<{ clinic_id: number; clinic_name: string; is_active: boolean }[]>
  >(token && groupId ? `/api/clinic-groups/${groupId}/clinics` : null, undefined, token ?? '')

  return {
    clinics: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get available clinics for assignment to a clinic group
export function useAvailableClinicsForGroup(
  groupId: number | null,
  params?: { search?: string; page?: number; pageSize?: number },
) {
  const { token } = useAuth()
  const queryParams = new URLSearchParams()
  if (params?.search) queryParams.append('search', params.search)
  if (params?.page) queryParams.append('page', String(params.page))
  if (params?.pageSize) queryParams.append('pageSize', String(params.pageSize))

  const queryString = queryParams.toString()
  const url = queryString
    ? `/api/clinic-groups/${groupId}/available-clinics?${queryString}`
    : `/api/clinic-groups/${groupId}/available-clinics`

  const { data, error, mutate } = useGet<
    ApiResponse<{
      clinics: Array<{
        clinic_id: number
        clinic_name: string
        address: string
        phone: string
      }>
      total: number
      page: number
      pageSize: number
    }>
  >(token && groupId ? url : null, undefined, token ?? '')

  return {
    clinics: data?.data?.clinics,
    total: data?.data?.total,
    page: data?.data?.page,
    pageSize: data?.data?.pageSize,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
